import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stDataPurgeCapability,
  stDeleteSystemConfig,
  stPurgeAllData,
  stSystemConfigs,
  stUpsertSystemConfig,
  StSystemConfig,
} from '../../../services/socialTradingService';
import { StActionButton, StActionRow, StScrollTable, stFormatDateTime, stText } from '../StListUi';

const PROTECTED_KEYS = new Set(['redpacket.claim_threshold']);
const PAGE_SIZE = 20;
const PURGE_CONFIRM_PHRASE = 'PURGE ALL DATA';

export default function SocialTradingSystemConfigsList() {
  const t = useTranslate();
  const notify = useNotify();
  const [items, setItems] = useState<StSystemConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [keyPrefix, setKeyPrefix] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editIsCreate, setEditIsCreate] = useState(true);
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('{}');
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [purgeEnabled, setPurgeEnabled] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePhrase, setPurgePhrase] = useState('');
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await stSystemConfigs({
        keyPrefix: keyPrefix.trim() || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [keyPrefix, page, notify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cap = await stDataPurgeCapability();
        if (!cancelled) setPurgeEnabled(!!cap?.enabled);
      } catch {
        if (!cancelled) setPurgeEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openCreate = () => {
    setEditIsCreate(true);
    setEditKey('');
    setEditValue('{\n  \n}');
    setEditOpen(true);
  };

  const openEdit = (row: StSystemConfig) => {
    setEditIsCreate(false);
    setEditKey(row.key);
    setEditValue(JSON.stringify(row.value ?? {}, null, 2));
    setEditOpen(true);
  };

  const save = async () => {
    const key = editKey.trim();
    if (!key) {
      notify(t('module.socialTrading.systemConfigKeyRequired'), { type: 'warning' });
      return;
    }
    let value: unknown;
    try {
      value = JSON.parse(editValue);
    } catch {
      notify(t('module.socialTrading.systemConfigInvalidJson'), { type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await stUpsertSystemConfig({ key, value });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setEditOpen(false);
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteKey) return;
    try {
      await stDeleteSystemConfig(deleteKey);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setDeleteKey(null);
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const closePurge = () => {
    if (purging) return;
    setPurgeOpen(false);
    setPurgePhrase('');
  };

  const confirmPurge = async () => {
    if (purgePhrase !== PURGE_CONFIRM_PHRASE) return;
    setPurging(true);
    try {
      await stPurgeAllData(PURGE_CONFIRM_PHRASE);
      notify(t('module.socialTrading.purgeAllDataOk'), { type: 'success' });
      setPurgeOpen(false);
      setPurgePhrase('');
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setPurging(false);
    }
  };

  return (
    <PermissionGuard resource="st-system-configs" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.systemConfigs')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.systemConfigs') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              {purgeEnabled && (
                <PermissionButton resource="st-system-configs" action="purge">
                  <Button size="small" variant="outlined" color="error" onClick={() => setPurgeOpen(true)}>
                    {t('module.socialTrading.purgeAllData')}
                  </Button>
                </PermissionButton>
              )}
              <Button size="small" variant="outlined" onClick={load}>
                {t('module.socialTrading.refresh')}
              </Button>
              <PermissionButton resource="st-system-configs" action="update">
                <Button size="small" variant="contained" onClick={openCreate}>
                  {t('module.socialTrading.systemConfigCreate')}
                </Button>
              </PermissionButton>
            </Stack>
          }
        />

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField
                size="small"
                label={t('module.socialTrading.systemConfigKeyPrefix')}
                value={keyPrefix}
                onChange={(e) => {
                  setKeyPrefix(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  if (page !== 0) setPage(0);
                  else load();
                }}
              >
                {t('module.socialTrading.search')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.systemConfigTotal', { total })}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <StScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.socialTrading.colKey')}</TableCell>
                  <TableCell>{t('module.socialTrading.colValuePreview')}</TableCell>
                  <TableCell>{t('module.socialTrading.colUpdated')}</TableCell>
                  <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {row.key}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        noWrap
                        title={JSON.stringify(row.value)}
                      >
                        {JSON.stringify(row.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>{stText(stFormatDateTime(row.updatedAt))}</TableCell>
                    <TableCell align="right">
                      <StActionRow>
                        <PermissionButton resource="st-system-configs" action="update">
                          <StActionButton onClick={() => openEdit(row)}>
                            {t('module.socialTrading.edit')}
                          </StActionButton>
                          <StActionButton
                            color="error"
                            disabled={PROTECTED_KEYS.has(row.key)}
                            onClick={() => setDeleteKey(row.key)}
                          >
                            {t('module.socialTrading.delete')}
                          </StActionButton>
                        </PermissionButton>
                      </StActionRow>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary">{t('module.socialTrading.emptySystemConfigs')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StScrollTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </CardContent>
        </Card>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{t('module.socialTrading.systemConfigEdit')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('module.socialTrading.colKey')}
                value={editKey}
                onChange={(e) => setEditKey(e.target.value)}
                fullWidth
                disabled={!editIsCreate}
              />
              <TextField
                label={t('module.socialTrading.colValue')}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                fullWidth
                multiline
                minRows={12}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            <PermissionButton resource="st-system-configs" action="update">
              <Button variant="contained" disabled={saving} onClick={save}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteKey}
          onClose={() => setDeleteKey(null)}
          title={t('module.socialTrading.confirmDeleteSystemConfig')}
          content={deleteKey || ''}
          action={
            <PermissionButton resource="st-system-configs" action="update">
              <Button variant="contained" color="error" onClick={confirmDelete}>
                {t('module.socialTrading.delete')}
              </Button>
            </PermissionButton>
          }
        />

        <ConfirmDialog
          open={purgeOpen}
          onClose={closePurge}
          title={t('module.socialTrading.purgeAllDataConfirmTitle')}
          content={
            <Stack spacing={2}>
              <Typography variant="body2">{t('module.socialTrading.purgeAllDataConfirmContent')}</Typography>
              <TextField
                size="small"
                label={t('module.socialTrading.purgeAllDataPhraseHint')}
                value={purgePhrase}
                onChange={(e) => setPurgePhrase(e.target.value)}
                fullWidth
                autoComplete="off"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Stack>
          }
          action={
            <PermissionButton resource="st-system-configs" action="purge">
              <Button
                variant="contained"
                color="error"
                disabled={purging || purgePhrase !== PURGE_CONFIRM_PHRASE}
                onClick={confirmPurge}
              >
                {t('module.socialTrading.purgeAllData')}
              </Button>
            </PermissionButton>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
